import type { ApiErrorBody } from './types'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly requestId?: string
  readonly details?: unknown

  constructor(status: number, body: ApiErrorBody['error'] | undefined, fallback: string) {
    super(body?.message ?? fallback)
    this.name = 'ApiError'
    this.status = status
    this.code = body?.code ?? 'UNKNOWN'
    this.requestId = body?.requestId
    this.details = body?.details
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  /** Multipart body (file uploads). Mutually exclusive with `body`. */
  form?: FormData
  signal?: AbortSignal
}

function csrfToken(): string | null {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? null
}

/**
 * Minimal fetch wrapper for the Rails JSON API.
 * - same-origin, cookies included (player sessions arrive in a later milestone)
 * - JSON request/response handling
 * - Rails CSRF token forwarded on mutating requests
 * - non-2xx responses become ApiError with the server's error envelope
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (method !== 'GET') {
    const token = csrfToken()
    if (token) headers['X-CSRF-Token'] = token
  }

  const res = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    body: options.form ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
    signal: options.signal,
  })

  if (!res.ok) {
    let body: ApiErrorBody | undefined
    try {
      body = (await res.json()) as ApiErrorBody
    } catch {
      body = undefined
    }
    throw new ApiError(res.status, body?.error, `Request failed with status ${res.status}`)
  }

  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export type UploadProgress = (fraction: number) => void

/**
 * Multipart upload with progress events (fetch has no upload progress). Same
 * error envelope handling as request().
 */
export function uploadWithProgress<T>(path: string, form: FormData, onProgress?: UploadProgress): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', path)
    xhr.responseType = 'json'
    xhr.setRequestHeader('Accept', 'application/json')
    const token = csrfToken()
    if (token) xhr.setRequestHeader('X-CSRF-Token', token)
    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) onProgress(e.loaded / e.total)
    }
    xhr.onerror = () => reject(new Error('Network error during upload'))
    xhr.onload = () => {
      const body = xhr.response as unknown
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(body as T)
      } else {
        const envelope = body && typeof body === 'object' ? (body as ApiErrorBody).error : undefined
        reject(new ApiError(xhr.status, envelope, `Upload failed with status ${xhr.status}`))
      }
    }
    xhr.send(form)
  })
}
